/**
 * Studio-first PDP behaviour (snippets/studio-cta.liquid, LIN-274).
 *
 * - Keeps every WhatsApp link's pre-filled message in sync with what the customer actually
 *   picked. Built from the variant picker, not the variant: ring sizes start on an
 *   "unselected" placeholder while the form already holds the first variant, and the
 *   message must not claim a size nobody chose.
 * - Computes the "open now" line in Asia/Jerusalem from the `custom.studio_hours`
 *   shop metafield. Shape:
 *     {
 *       "source": "manual" | "places",
 *       "updated_at": "2026-09-14T08:00:00Z",
 *       "weekly": { "0": [["10:00","19:00"]], ..., "6": [] },   // 0 = Sunday
 *       "special": { "2026-10-02": [] }                          // overrides a date; [] = closed
 *     }
 *   Missing, malformed, or a Places sync older than 72h → the line stays hidden.
 * - Opens the navigation dialog and copies the address.
 * - Dispatches `studio:view` (buy box seen, sticky bar shown — once each) and `studio:action`
 *   on every tap, on document. assets/studio-tracking.js turns those into GTM / Meta / lead
 *   beacon calls; this file knows nothing about them.
 *
 * Loaded twice when both the card and the sticky bar render — the guard at the bottom keeps one instance.
 */

const TIMEZONE = 'Asia/Jerusalem';
const PLACES_STALE_MS = 72 * 60 * 60 * 1000;

function init() {
  const card = document.querySelector('.studio-cta--card');
  if (!card) return;

  const state = {
    productId: card.dataset.productId,
    productTitle: card.dataset.productTitle || '',
    productUrl: card.dataset.productUrl || '',
    variantId: card.dataset.variantId || '',
    variantTitle: card.dataset.variantTitle || '',
    price: Number(card.dataset.price) || null,
  };

  state.openNow = renderStatus(card);
  refreshWhatsapp(card, state);
  bindVariantUpdates(card, state);
  bindClicks(card, state);
  observeView(card, state);
}

/* ---------------------------------------------------------------- WhatsApp */

/** Selected option labels from the main variant picker; skips placeholders ("unselected", value ""). */
function readSelection(state) {
  const picker =
    document.querySelector(`variant-picker[data-product-id="${state.productId}"]`) ||
    document.querySelector('variant-picker');
  if (!picker) return { label: state.variantTitle, complete: true };

  const parts = [];
  let complete = true;
  picker.querySelectorAll('select').forEach((select) => {
    const option = select.options[select.selectedIndex];
    if (!select.value || !option || option.dataset.optionValueId === 'unselected') {
      complete = false;
      return;
    }
    parts.push(option.textContent.trim());
  });
  picker.querySelectorAll('fieldset').forEach((fieldset) => {
    const checked = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));
    if (!checked) {
      complete = false;
      return;
    }
    parts.push(checked.value);
  });
  return { label: parts.join(' / '), complete };
}

function refreshWhatsapp(card, state) {
  const selection = readSelection(state);
  state.variantTitle = selection.label;
  state.selectionComplete = selection.complete;
  const href = whatsappHref(card, state);
  document.querySelectorAll('[data-studio-action="whatsapp"]').forEach((link) => link.setAttribute('href', href));
}

function whatsappHref(card, state) {
  const label = state.variantTitle ? `${state.productTitle} (${state.variantTitle})` : state.productTitle;
  const url = new URL(state.productUrl, window.location.origin);
  // Only point at a variant the customer fully chose.
  if (state.variantId && state.selectionComplete) url.searchParams.set('variant', state.variantId);
  // Product link on its own line, so WhatsApp renders its preview under the request.
  const message = `${card.dataset.messageTemplate.replace('__PRODUCT__', label)}\n${url.toString()}`;
  return `https://wa.me/${card.dataset.whatsappPhone}?text=${encodeURIComponent(message)}`;
}

function bindVariantUpdates(card, state) {
  document.addEventListener('variant:update', (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail?.resource) return;
    if (detail.data?.productId && String(detail.data.productId) !== String(state.productId)) return;

    const variant = detail.resource;
    state.variantId = String(variant.id ?? state.variantId);
    if (typeof variant.price === 'number') state.price = variant.price / 100;
    // The picker is morphed with the new variant's HTML around this event — read it after.
    requestAnimationFrame(() => refreshWhatsapp(card, state));
  });

  // Choosing a size can leave the variant unchanged (placeholder → first size), so no variant:update.
  document.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.closest('variant-picker')) {
      requestAnimationFrame(() => refreshWhatsapp(card, state));
    }
  });
}

/* ------------------------------------------------------------------- Clicks */

function bindClicks(card, state) {
  const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById('StudioNavDialog'));

  document.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element)) return;

    const opener = target.closest('[data-studio-open-nav]');
    if (opener && dialog) {
      emit('studio:action', state, { action: 'open_nav', placement: placementOf(opener) });
      dialog.showModal();
      return;
    }

    if (dialog && (target.closest('[data-studio-close-nav]') || target === dialog)) {
      dialog.close();
      return;
    }

    const actionEl = target.closest('[data-studio-action]');
    if (!actionEl) return;

    const action = actionEl.getAttribute('data-studio-action');
    emit('studio:action', state, {
      action,
      placement: placementOf(actionEl),
      provider: actionEl.getAttribute('data-studio-provider') || undefined,
    });

    const address = actionEl.getAttribute('data-studio-copy-address');
    if (address) copyAddress(card, address);
  });
}

function placementOf(el) {
  return el.closest('[data-studio-placement]')?.getAttribute('data-studio-placement') || 'unknown';
}

function copyAddress(card, address) {
  const toast = document.querySelector('[data-studio-toast]');
  const show = (text) => {
    if (!toast) return;
    toast.textContent = text;
    toast.classList.add('is-visible');
    clearTimeout(copyAddress.timer);
    copyAddress.timer = setTimeout(() => toast.classList.remove('is-visible'), 1800);
  };
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(address).then(() => show(card.dataset.labelCopied), () => show(address));
  } else {
    show(address);
  }
}

/* --------------------------------------------------------------- Analytics */

function emit(name, state, extra = {}) {
  document.dispatchEvent(
    new CustomEvent(name, {
      detail: {
        product_id: state.productId,
        product_title: state.productTitle,
        pdp_mode: 'studio',
        // The form holds the first variant before a size is chosen — only report one the shopper picked.
        variant_id: state.selectionComplete === false ? null : state.variantId,
        // Always a real variant: the Meta and Google catalogs (Multifeed) are keyed by variant id.
        catalog_variant_id: state.variantId,
        variant_title: state.variantTitle,
        price: state.price,
        currency: 'ILS',
        open_now: state.openNow ?? null,
        ...extra,
      },
    })
  );
}

function observeView(card, state) {
  if (!('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting)) {
      observer.disconnect();
      emit('studio:view', state, { placement: 'buybox' });
    }
  }, { threshold: 0.5 });
  observer.observe(card);

  // The sticky bar slides in via an `is-active` class (sticky-add-to-cart.js) — report the first time.
  const sticky = document.querySelector('sticky-add-to-cart.sticky-add-to-cart--studio');
  if (sticky) {
    const stickyObserver = new MutationObserver(() => {
      if (sticky.classList.contains('is-active')) {
        stickyObserver.disconnect();
        emit('studio:view', state, { placement: 'sticky' });
      }
    });
    stickyObserver.observe(sticky, { attributes: true, attributeFilter: ['class'] });
  }
}

/* ------------------------------------------------------------------- Hours */

function readHours() {
  try {
    const raw = document.getElementById('StudioHours')?.textContent?.trim();
    if (!raw || raw === 'null') return null;
    const hours = JSON.parse(raw);
    if (!hours || typeof hours.weekly !== 'object') return null;
    if (hours.source === 'places') {
      const updated = Date.parse(hours.updated_at);
      if (!updated || Date.now() - updated > PLACES_STALE_MS) return null;
    }
    return hours;
  } catch {
    return null;
  }
}

/** Current wall-clock in Jerusalem: { dateKey: 'YYYY-MM-DD', dow: 0-6, minutes } */
function jerusalemNow(offsetDays = 0) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .map((p) => [p.type, p.value])
  );
  const base = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + offsetDays));
  return {
    dateKey: base.toISOString().slice(0, 10),
    dow: base.getUTCDay(),
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
    date: base,
  };
}

const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + (m || 0);
};

function intervalsFor(hours, day) {
  const special = hours.special?.[day.dateKey];
  const list = Array.isArray(special) ? special : hours.weekly[String(day.dow)];
  return Array.isArray(list) ? list.filter((i) => Array.isArray(i) && i.length === 2) : [];
}

function computeStatus(hours) {
  const today = jerusalemNow();
  for (const [open, close] of intervalsFor(hours, today)) {
    if (today.minutes >= toMinutes(open) && today.minutes < toMinutes(close)) {
      return { open: true, key: 'labelOpenUntil', time: close };
    }
  }
  const laterToday = intervalsFor(hours, today).find(([open]) => toMinutes(open) > today.minutes);
  if (laterToday) return { open: false, key: 'labelOpensToday', time: laterToday[0] };

  for (let offset = 1; offset <= 7; offset++) {
    const day = jerusalemNow(offset);
    const first = intervalsFor(hours, day)[0];
    if (!first) continue;
    if (offset === 1) return { open: false, key: 'labelOpensTomorrow', time: first[0] };
    const dayName = new Intl.DateTimeFormat(document.documentElement.lang || 'he', {
      weekday: 'long',
      timeZone: 'UTC',
    }).format(day.date);
    return { open: false, key: 'labelOpensDay', time: first[0], day: dayName };
  }
  return null;
}

function renderStatus(card) {
  const hours = readHours();
  const status = hours && computeStatus(hours);
  if (!status) return null;

  const text = card.dataset[status.key]
    .replace('__TIME__', status.time)
    .replace('__DAY__', status.day || '');

  document.querySelectorAll('[data-studio-status]').forEach((el) => {
    el.classList.toggle('is-closed', !status.open);
    const label = el.querySelector('[data-studio-status-text]');
    if (label) label.textContent = text;
    el.hidden = false;
  });
  return status.open;
}

/* Last, so every const above is initialised before init() touches it. */
if (!window.__studioCtaLoaded) {
  window.__studioCtaLoaded = true;
  init();
}
